import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  LEADARTNAME,
  LEADSTATUSNAME,
  VERDACHTSGRUND,
  akte as ladeAkte,
  erledigen,
  liste as ladeListe,
  uebernehmen,
  verwerfen,
  zuordnen,
  type Leadakte,
  type Leadentscheidung,
  type Leadfolgen,
  type Leadneukunde,
  type Leadstatus,
  type Leadwiedervorlage,
  type Leadzeile,
} from '../api/leads';
import { ApiFehler } from '../api/fehler';
import { alsZeitpunkt, heute } from '../datum';
import { AbTablet } from '../komponenten/AbTablet';
import { markenklasse, type Markenart } from '../marken';
import { Feldraster, Feldzeile } from '../komponenten/Akte';
import { Liste } from '../komponenten/Liste';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { useZaehler, zaehlwort } from '../komponenten/Seitenkontext';
import { KundeVerweis } from '../komponenten/Kontaktkarte';

/**
 * Die Webanfragen (WEB-02, KUN-23).
 *
 * Was aus den Formularen des Portals kommt — Kontakt, Rückruf,
 * Firmenanfrage, Bewerbung —, landet hier und NICHT als Kunde im Bestand.
 * Die Dublettenprüfung hat beim Eingang schon nachgesehen; ihr Ergebnis steht
 * an der Anfrage als Vorschlag. Der Vertrieb entscheidet: einem Kunden
 * zuordnen, als neuen Kunden anlegen, erledigen oder verwerfen. Jede Anfrage
 * wird einmal entschieden. Mit der Übernahme entsteht auf Wunsch eine
 * Wiedervorlage (KUN-15); danach führt „Angebot anlegen“ weiter (WEB-02).
 *
 * SEITENTYP LISTE (ÄA-41): Zähler im Seitenkopf, der Schalter in der
 * Filterleiste, die Tabelle in der Karte. Die geöffnete Anfrage steht in der
 * Vorschau rechts — mit Dublettenvorschlag und Entscheidung, ohne dass die
 * Liste aus dem Blick gerät.
 */

/** Offen ist zu tun; zugeordnet und erledigt sind am Ziel, verworfen ist still. */
const STATUSMARKE: Readonly<Record<Leadstatus, Markenart>> = {
  neu: 'aufmerksamkeit',
  zugeordnet: 'abschluss',
  erledigt: 'abschluss',
  verworfen: 'stillgelegt',
};
export function Webanfragen({
  darfBearbeiten,
  beiKunde,
  beiAngebot,
  zurKunde,
}: {
  readonly darfBearbeiten: boolean;
  readonly beiKunde: (id: string) => void;
  /** Auftrag CRM-181: die Kontaktkarte am Dublettenverdacht — nur mit kunde/sehen. */
  readonly zurKunde?: ((id: string) => void) | undefined;
  /** WEB-02: aus der zugeordneten Anfrage ein Angebot für ihren Kunden; fehlt ohne `angebot/anlegen`. */
  readonly beiAngebot?: ((kunde: string) => void) | undefined;
}): JSX.Element {
  const [nurOffene, setzeNurOffene] = useState(true);
  const [liste, setzeListe] = useState<readonly Leadzeile[]>([]);
  const [geladen, setzeGeladen] = useState(false);
  const [offen, setzeOffen] = useState<Leadakte | null>(null);
  const [folgen, setzeFolgen] = useState<Leadfolgen | null>(null);
  const [grund, setzeGrund] = useState('');
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);

  const melde = useCallback((e: unknown) => {
    if (e instanceof ApiFehler) setzeFehler(e);
    else throw e;
  }, []);

  const holen = useCallback(async () => {
    try {
      setzeListe(await ladeListe(nurOffene ? 'neu' : null));
      setzeFehler(null);
    } catch (e) {
      melde(e);
    } finally {
      setzeGeladen(true);
    }
  }, [nurOffene, melde]);

  useEffect(() => {
    void holen();
  }, [holen]);

  const oeffnen = (id: string): void => {
    setzeGrund('');
    setzeFolgen(null);
    void ladeAkte(id).then(setzeOffen).catch(melde);
  };

  /** Die entschiedene Anfrage zeigen und die Liste nachladen (`holen` meldet selbst). */
  const entschieden = (neu: Leadentscheidung): void => {
    setzeOffen(neu);
    setzeFolgen(neu.folgen ?? null);
    setzeGrund('');
    void holen();
  };

  const entscheiden = (vorgang: Promise<Leadentscheidung>): void => {
    void vorgang.then(entschieden).catch(melde);
  };

  /* ÄA-41: der Zähler im Seitenkopf — erst, wenn geladen ist. */
  useZaehler(geladen ? zaehlwort(liste.length, 'Anfrage', 'Anfragen') : undefined);

  return (
    <>
      {fehler !== null && <Meldung fehler={fehler} />}

      <Liste
        filter={
          <label className="liste__schalter">
            <input
              type="checkbox"
              checked={nurOffene}
              onChange={(e) => {
                setzeNurOffene(e.target.checked);
              }}
            />
            <span>Nur offene Anfragen</span>
          </label>
        }
        hinweise={
          <p className="hilfe">
            Anfragen aus den Formularen des Auftritts. Sie legen keinen Kunden an; die Prüfung auf
            Dubletten schlägt vor, wem sie gehören könnten.
          </p>
        }
        vorschau={
          offen === null ? (
            <p className="feld__hilfe">
              Öffnen Sie eine Anfrage über ihre Nummer — hier stehen dann ihre Angaben, der
              Dublettenvorschlag und die Entscheidung.
            </p>
          ) : (
            <Anfrage
              offen={offen}
              folgen={folgen}
              grund={grund}
              darfBearbeiten={darfBearbeiten}
              beiGrund={setzeGrund}
              beiKunde={beiKunde}
              beiAngebot={beiAngebot}
              zurKunde={zurKunde}
              entscheiden={entscheiden}
              beiEntschieden={entschieden}
            />
          )
        }
      >
        {liste.length === 0 ? (
          geladen && (
            <div className="liste__leer" role="status">
              <p>{nurOffene ? 'Keine offenen Anfragen.' : 'Noch keine Anfragen.'}</p>
            </div>
          )
        ) : (
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle" aria-label="Webanfragen">
              <thead>
                <tr>
                  <th scope="col" className="liste__spalte--knapp">
                    Nummer
                  </th>
                  <th scope="col">Eingang</th>
                  <th scope="col">Art</th>
                  <th scope="col">Name</th>
                  <th scope="col">Kontakt</th>
                  <th scope="col">Verdacht</th>
                  <th scope="col">Stand</th>
                </tr>
              </thead>
              <tbody>
                {liste.map((z) => (
                  <tr
                    key={z.id}
                    className={
                      offen?.id === z.id
                        ? 'liste__zeile--gewaehlt'
                        : z.status === 'neu'
                          ? undefined
                          : 'liste__zeile--blass'
                    }
                  >
                    <th scope="row" className="liste__spalte--knapp">
                      <button
                        type="button"
                        className="liste__oeffnen liste__nummer"
                        onClick={() => {
                          oeffnen(z.id);
                        }}
                      >
                        {z.vorgangsnummer}
                      </button>
                    </th>
                    <td className="liste__einzeilig">{alsZeitpunkt(z.eingangAm)}</td>
                    <td>{LEADARTNAME[z.art]}</td>
                    <td className="liste__name">{z.name}</td>
                    <td>
                      {z.email}
                      {z.telefon !== null && <> · {z.telefon}</>}
                    </td>
                    {/* KUN-23: der Dublettenverdacht beim Eingang. */}
                    <td>
                      {z.verdachtsfaelle === 0 ? (
                        '—'
                      ) : (
                        <span className={markenklasse('aufmerksamkeit')}>
                          {`${String(z.verdachtsfaelle)} Treffer`}
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={markenklasse(STATUSMARKE[z.status])}>
                        {LEADSTATUSNAME[z.status]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Liste>
    </>
  );
}

/**
 * Die geöffnete Anfrage in der Vorschau.
 *
 * NUR „VERWERFEN …" FRAGT IM POP-UP NACH (CRM-104): Die Anfrage wird ohne
 * Kunden abgelegt, lässt sich nicht wieder öffnen und wird nach der Frist
 * gelöscht. Der Vermerk bleibt im Feld darüber; das Pop-up zeigt ihn an.
 * Zuordnen und „Erledigt" schließen die Bearbeitung im Haus ab, ohne etwas
 * nach außen zu schicken oder jemandem einen Zugang zu öffnen — sie fragen
 * nicht.
 */
function Anfrage({
  offen,
  folgen,
  grund,
  darfBearbeiten,
  beiGrund,
  beiKunde,
  beiAngebot,
  zurKunde,
  entscheiden,
  beiEntschieden,
}: {
  readonly offen: Leadakte;
  readonly folgen: Leadfolgen | null;
  readonly grund: string;
  readonly darfBearbeiten: boolean;
  readonly beiGrund: (grund: string) => void;
  readonly beiKunde: (id: string) => void;
  readonly beiAngebot: ((kunde: string) => void) | undefined;
  readonly zurKunde: ((id: string) => void) | undefined;
  readonly entscheiden: (vorgang: Promise<Leadentscheidung>) => void;
  /** Nach dem Verwerfen aus der Rückfrage — ein Fehler bleibt im Pop-up. */
  readonly beiEntschieden: (neu: Leadentscheidung) => void;
}): JSX.Element {
  const [fragt, setzeFragt] = useState(false);
  /* KUN-15: Wiedervorlage für mich, mit der Übernahme — angehakt beim Rückruf. */
  const [mitWiedervorlage, setzeMitWiedervorlage] = useState(offen.art === 'rueckruf');
  const [faelligAm, setzeFaelligAm] = useState(heute());
  const wiedervorlage: Leadwiedervorlage | null =
    mitWiedervorlage && faelligAm !== '' ? { faelligAm } : null;
  const entscheidbar = darfBearbeiten && offen.status === 'neu';
  const uebernommenVon = offen.status === 'zugeordnet' ? offen.kunde : null;

  return (
    <section aria-label={`Anfrage ${offen.vorgangsnummer}`}>
      <div className="karte__titel">
        <h2>{LEADARTNAME[offen.art]}</h2>
        <small>{offen.vorgangsnummer}</small>
      </div>
      <Feldraster>
        <Feldzeile
          beschriftung="Von"
          wert={offen.name}
          anzeige={
            <>
              {offen.name} · {offen.email}
              {offen.telefon !== null && <> · {offen.telefon}</>}
            </>
          }
        />
        <Feldzeile
          beschriftung="Eingang"
          wert={offen.eingangAm}
          anzeige={`${alsZeitpunkt(offen.eingangAm)} über ${offen.portal}, Seite ${offen.quelle}`}
        />
        {offen.betreff !== null && <Feldzeile beschriftung="Betreff" wert={offen.betreff} />}
        {offen.erreichbarkeit !== null && (
          <Feldzeile beschriftung="Erreichbar" wert={offen.erreichbarkeit} />
        )}
        {/* LEG-12: werbliche Ansprache nur mit dokumentierter Einwilligung. */}
        <Feldzeile
          beschriftung="Werbung"
          wert={offen.werbeeinwilligung ? 'eingewilligt' : 'nicht eingewilligt'}
          anzeige={
            offen.werbeeinwilligung ? (
              <>
                eingewilligt
                {offen.einwilligung?.fassung != null && <> · Fassung {offen.einwilligung.fassung}</>}
                {offen.einwilligung?.erteiltAm != null && (
                  <> · am {alsZeitpunkt(offen.einwilligung.erteiltAm)}</>
                )}
              </>
            ) : (
              'nicht eingewilligt'
            )
          }
        />
      </Feldraster>
      {offen.nachricht !== null && <p>{offen.nachricht}</p>}
      {Object.keys(offen.angaben).length > 0 && (
        <details>
          <summary>Weitere Angaben aus dem Formular</summary>
          <pre>{JSON.stringify(offen.angaben, null, 2)}</pre>
        </details>
      )}

      {offen.einwilligung?.wortlaut != null && (
        <details>
          <summary>Wortlaut der Einwilligung</summary>
          <p>{offen.einwilligung.wortlaut}</p>
        </details>
      )}

      {entscheidbar && (
        <div className="feldreihe">
          <label className="liste__schalter">
            <input
              type="checkbox"
              checked={mitWiedervorlage}
              onChange={(e) => {
                setzeMitWiedervorlage(e.target.checked);
              }}
            />
            <span>Wiedervorlage für mich anlegen</span>
          </label>
          {mitWiedervorlage && (
            <label className="feld">
              <span className="feld__beschriftung">Fällig am</span>
              <input
                type="date"
                value={faelligAm}
                onChange={(e) => {
                  setzeFaelligAm(e.target.value);
                }}
              />
            </label>
          )}
        </div>
      )}

      <h3>Mögliche Dubletten</h3>
      {offen.dublettenverdacht.length === 0 ? (
        <p className="hilfe">Beim Eingang wurde kein passender Kunde gefunden.</p>
      ) : (
        <ul className="kanalliste">
          {offen.dublettenverdacht.map((v) => (
            <li key={v.kunde}>
              {/* WEB-02, KUN-23: Telefon und E-Mail des Verdachts neben der Anfrage vergleichen (CRM-181). */}
              <KundeVerweis kundeId={v.kunde} zurAkte={zurKunde}>
                <button
                  type="button"
                  className="liste__oeffnen"
                  onClick={() => {
                    beiKunde(v.kunde);
                  }}
                >
                  {v.kundennummer} {v.name}
                </button>
              </KundeVerweis>
              {v.ort !== null && <> · {v.ort}</>}{' '}
              <span className={markenklasse('laufend')}>{VERDACHTSGRUND[v.grund] ?? v.grund}</span>
              {entscheidbar && (
                <button
                  type="button"
                  className="knopf knopf--klein"
                  onClick={() => {
                    entscheiden(zuordnen(offen.id, v.kunde, wiedervorlage));
                  }}
                >
                  Diesem Kunden zuordnen
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {entscheidbar && (
        <AbTablet teil>
          <Neukunde
            offen={offen}
            beiAnlegen={(neu) => {
              entscheiden(uebernehmen(offen.id, neu, wiedervorlage));
            }}
          />
        </AbTablet>
      )}

      {offen.status === 'neu' ? (
        darfBearbeiten && (
          <>
            <label className="feld">
              <span className="feld__beschriftung">Vermerk (zum Verwerfen Pflicht)</span>
              <input
                value={grund}
                maxLength={500}
                onChange={(e) => {
                  beiGrund(e.target.value);
                }}
              />
            </label>
            <div className="knopfreihe">
              <button
                type="button"
                className="knopf knopf--eingriff"
                disabled={grund.trim() === ''}
                onClick={() => {
                  setzeFragt(true);
                }}
              >
                Verwerfen …
              </button>
              <button
                type="button"
                className="knopf knopf--haupt"
                onClick={() => {
                  entscheiden(erledigen(offen.id, grund));
                }}
              >
                Erledigt
              </button>
            </div>
          </>
        )
      ) : (
        <p className="hilfe" role="status">
          {LEADSTATUSNAME[offen.status]}
          {offen.kundennummer !== null && <> an {offen.kundennummer}</>}
          {offen.bearbeitetVon !== null && <> von {offen.bearbeitetVon}</>}
          {offen.bearbeitetAm !== null && <> am {alsZeitpunkt(offen.bearbeitetAm)}</>}
          {offen.bearbeitungsgrund !== null && <> — {offen.bearbeitungsgrund}</>}
        </p>
      )}

      {folgen !== null && <Folgen folgen={folgen} uebernahme={offen.uebernahme} />}

      {uebernommenVon !== null && (
        <div className="knopfreihe">
          <button
            type="button"
            className="knopf"
            data-auch-telefon
            onClick={() => {
              beiKunde(uebernommenVon);
            }}
          >
            Kundenakte öffnen
          </button>
          {beiAngebot !== undefined && (
            <button
              type="button"
              className="knopf knopf--haupt"
              onClick={() => {
                beiAngebot(uebernommenVon);
              }}
            >
              Angebot anlegen
            </button>
          )}
        </div>
      )}

      {fragt && (
        <Rueckfrage
          frage="Anfrage verwerfen?"
          unterzeile={`${offen.vorgangsnummer} · ${offen.name}`}
          tat="Verwerfen"
          beiBestaetigen={async () => {
            const neu = await verwerfen(offen.id, grund);
            setzeFragt(false);
            beiEntschieden(neu);
          }}
          beiSchliessen={() => {
            setzeFragt(false);
          }}
        >
          {/* WEB-02, Entscheidung 11a: verworfene Anfragen werden sechs Monate danach gelöscht. */}
          <p>
            Die Anfrage wird ohne Kunden abgelegt und lässt sich nicht wieder öffnen; sechs Monate
            danach wird sie gelöscht.
          </p>
          <p>Vermerk: „{grund.trim()}“</p>
        </Rueckfrage>
      )}
    </section>
  );
}

/** Was die Übernahme bewirkt hat, in einem Satz je Folge. */
function Folgen({
  folgen,
  uebernahme,
}: {
  readonly folgen: Leadfolgen;
  readonly uebernahme: Leadakte['uebernahme'];
}): JSX.Element {
  return (
    <ul className="hilfe">
      {uebernahme === 'neuanlage' && <li>Der Kunde wurde aus der Anfrage angelegt.</li>}
      {/* LEG-12 */}
      {folgen.werbung === 'uebernommen' && (
        <li>Die Einwilligung in Werbung steht jetzt mit ihrem Nachweis am Kunden.</li>
      )}
      {folgen.werbung === 'widerspruch_bleibt' && (
        <li>
          Der Kunde hat der Werbung widersprochen. Die Einwilligung aus dem Formular hebt das nicht
          auf; ändern Sie es bei Bedarf in der Kundenakte.
        </li>
      )}
      {/* KUN-15 */}
      {folgen.wiedervorlage !== null && <li>Die Wiedervorlage steht in „Meine Wiedervorlagen“.</li>}
    </ul>
  );
}

/** Vor- und Nachname aus einer Zeile: das letzte Wort ist der Nachname. */
function namensteile(name: string): { readonly vorname: string; readonly nachname: string } {
  const teile = name.trim().split(/\s+/);
  const nachname = teile.pop() ?? '';
  return { vorname: teile.join(' '), nachname };
}

/**
 * Als neuen Kunden anlegen (WEB-02).
 *
 * Vorbelegt aus dem Formular: Name, bei der Firmenanfrage Firma und
 * Kundenart „Geschäft“. Kein Pop-up (Tabelle 29): Ein angelegter Kunde lässt
 * sich ändern, zusammenführen und löschen.
 */
function Neukunde({
  offen,
  beiAnlegen,
}: {
  readonly offen: Leadakte;
  readonly beiAnlegen: (neu: Leadneukunde) => void;
}): JSX.Element {
  const firma = (offen.angaben['firmenanfrage'] as { firma?: unknown } | undefined)?.firma;
  const [kundenart, setzeKundenart] = useState<'privat' | 'geschaeft'>(
    offen.art === 'firmenanfrage' ? 'geschaeft' : 'privat',
  );
  const [firmierung, setzeFirmierung] = useState(typeof firma === 'string' ? firma : '');
  const [name, setzeName] = useState(() => namensteile(offen.name));
  const vollstaendig =
    name.nachname.trim() !== '' && (kundenart === 'privat' || firmierung.trim() !== '');

  return (
    <section aria-labelledby="webanfrage-neukunde">
      <h3 id="webanfrage-neukunde">Als neuen Kunden anlegen</h3>
      <label className="feld">
        <span className="feld__beschriftung">Kundenart</span>
        <select
          value={kundenart}
          onChange={(e) => {
            setzeKundenart(e.target.value === 'geschaeft' ? 'geschaeft' : 'privat');
          }}
        >
          <option value="privat">Privat</option>
          <option value="geschaeft">Geschäft</option>
        </select>
      </label>
      {kundenart === 'geschaeft' && (
        <label className="feld">
          <span className="feld__beschriftung">Firmierung</span>
          <input
            value={firmierung}
            maxLength={160}
            onChange={(e) => {
              setzeFirmierung(e.target.value);
            }}
          />
        </label>
      )}
      <label className="feld">
        <span className="feld__beschriftung">
          {kundenart === 'geschaeft' ? 'Vorname des Ansprechpartners' : 'Vorname'}
        </span>
        <input
          value={name.vorname}
          maxLength={120}
          onChange={(e) => {
            setzeName({ ...name, vorname: e.target.value });
          }}
        />
      </label>
      <label className="feld">
        <span className="feld__beschriftung">
          {kundenart === 'geschaeft' ? 'Nachname des Ansprechpartners' : 'Nachname'}
        </span>
        <input
          value={name.nachname}
          maxLength={120}
          onChange={(e) => {
            setzeName({ ...name, nachname: e.target.value });
          }}
        />
      </label>
      <p className="feld__hilfe">
        E-Mail und Telefon kommen aus der Anfrage
        {offen.art === 'firmenanfrage' && ', die Anschrift aus der Firmenanfrage'}.
        {offen.dublettenverdacht.length > 0 && ' Prüfen Sie vorher die möglichen Dubletten oben.'}
      </p>
      <div className="knopfreihe">
        <button
          type="button"
          className="knopf"
          disabled={!vollstaendig}
          onClick={() => {
            const vorname = name.vorname.trim();
            beiAnlegen(
              kundenart === 'privat'
                ? { kundenart, nachname: name.nachname.trim(), ...(vorname !== '' ? { vorname } : {}) }
                : {
                    kundenart,
                    firmierung: firmierung.trim(),
                    nachname: name.nachname.trim(),
                    ...(vorname !== '' ? { vorname } : {}),
                  },
            );
          }}
        >
          Als Kunden anlegen
        </button>
      </div>
    </section>
  );
}
